<template>
  <div class="page">
    <header class="header">
      <el-button text class="back-btn" @click="handleBack" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="back-icon" />
      </el-button>
      <h1 class="title">备料箱码校验</h1>
      <div class="header-right"></div>
    </header>

    <main class="content">
      <el-steps
        :active="currentStep - 1"
        align-center
        finish-status="success"
        class="steps"
      >
        <el-step title="扫备料标签" />
        <el-step title="扫箱码" />
      </el-steps>

      <!-- 步骤一：扫备料标签 -->
      <el-card class="section" shadow="never">
        <template #header>
          <div class="section-title">备料标签</div>
        </template>
        <div class="scan-row">
          <el-input
            ref="tagInputRef"
            v-model="tagInput"
            class="scan-input"
            placeholder="扫备料标签"
            clearable
            :disabled="tagLoading"
            @keyup.enter="handleTagManual"
          />
          <el-button
            class="scan-btn"
            type="primary"
            :loading="tagLoading"
            :disabled="tagLoading"
            @click="openScan('tag')"
          >
            <img
              v-if="!tagLoading"
              src="@/assets/扫码.svg"
              alt="扫码"
              class="scan-icon"
            />
          </el-button>
        </div>

        <el-descriptions
          v-if="tagInfo"
          :column="1"
          border
          size="small"
          class="info-desc"
        >
          <el-descriptions-item label="物料编码">
            <div class="material-row">
              <span class="material-no">{{ tagInfo.materialNo || '-' }}</span>
              <el-button
                class="material-image-btn"
                size="small"
                @click="handleQueryImage"
              >
                图片
              </el-button>
            </div>
          </el-descriptions-item>
          <el-descriptions-item label="供应商">
            {{ supplierInfo?.supplierName || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="数量">
            <template v-if="hasQtyRule">
              <span
                :class="{
                  'qty-ok': scannedQtySum < tagQtyLimit,
                  'qty-full': scannedQtySum >= tagQtyLimit && tagQtyLimit > 0
                }"
              >
                {{ scannedQtySum }} / {{ tagQtyLimit || tagInfo.num || '-' }}
              </span>
            </template>
            <template v-else>
              {{ tagInfo.num || '-' }}
            </template>
          </el-descriptions-item>
        </el-descriptions>
      </el-card>

      <!-- 步骤二：扫箱码 -->
      <el-card v-if="tagInfo && qrConfigReady" class="section" shadow="never">
        <template #header>
          <div class="section-title">扫箱码</div>
        </template>
        <div class="scan-row">
          <el-input
            ref="boxInputRef"
            v-model="boxInput"
            class="scan-input"
            placeholder="扫箱码"
            clearable
            @keyup.enter="handleBoxManual"
          />
          <el-button
            class="scan-btn"
            type="primary"
            @click="openScan('box')"
          >
            <img src="@/assets/扫码.svg" alt="扫码" class="scan-icon" />
          </el-button>
        </div>

        <el-empty
          v-if="checkResults.length === 0"
          description="暂未扫描箱码"
          :image-size="60"
        />
        <div v-else class="scanned-list">
          <div
            v-for="(item, idx) in checkResults"
            :key="item.id"
            class="scanned-item"
            :class="{ fail: !item.matched }"
          >
            <span class="scanned-idx" :class="{ fail: !item.matched }">
              {{ idx + 1 }}
            </span>
            <div class="scanned-body">
              <span class="scanned-code">
                {{ item.materialNo || '-' }}
                <template v-if="item.boxIndex">
                  · 箱码 {{ item.boxIndex }}
                </template>
                <template v-if="item.num !== '' && item.num != null">
                  · 数量 {{ item.num }}
                </template>
              </span>
              <span class="scanned-raw">{{ item.raw }}</span>
              <span v-if="item.failReason" class="scanned-fail-reason">
                {{ item.failReason }}
              </span>
            </div>
            <el-tag :type="item.matched ? 'success' : 'danger'" size="small">
              {{ item.matched ? '匹配' : '不匹配' }}
            </el-tag>
            <el-button
              class="scanned-del"
              text
              size="small"
              @click="removeResult(idx)"
            >
              ×
            </el-button>
          </div>
        </div>
      </el-card>
    </main>

    <div class="bottom-bar">
      <el-button class="bottom-btn secondary" @click="handleReset">
        重新开始
      </el-button>
    </div>

    <CameraScan
      v-model:visible="scanVisible"
      :title="scanTitle"
      :formats="scanFormats"
      tip-text="将二维码放入框内，靠近一些识别更快"
      @success="handleScanSuccess"
      @close="scanVisible = false"
    />

    <!-- 物料图片弹窗 -->
    <el-dialog
      v-model="imageDialogVisible"
      title="物料图片"
      width="90%"
      :close-on-click-modal="true"
      class="material-image-dialog"
    >
      <div
        v-if="imageLoading"
        v-loading="imageLoading"
        element-loading-text="加载中..."
        class="image-loading"
      ></div>
      <template v-else>
        <div v-if="currentImageMaterialNo" class="image-material-info">
          <span class="image-material-label">物料编码：</span>
          <span class="image-material-value">{{ currentImageMaterialNo }}</span>
        </div>
        <div v-if="displayImageUrl" class="image-preview-area">
          <el-image
            :src="displayImageUrl"
            :preview-src-list="[displayImageUrl]"
            fit="contain"
            class="material-image"
            preview-teleported
          />
          <div class="image-zoom-hint">点击图片查看大图</div>
        </div>
        <el-empty v-else description="暂无物料图片" :image-size="80" />
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, computed, nextTick, watch, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { Html5QrcodeSupportedFormats } from 'html5-qrcode'
import CameraScan from '@/components/CameraScan'
import {
  getSupplierPageGtZero,
  getMaterialPage
} from '@/api/wareInReceiveCreate'
import service from '@/utils/request'

const SCAN_FORMATS = [
  Html5QrcodeSupportedFormats.QR_CODE,
  Html5QrcodeSupportedFormats.CODE_128,
]

/** 解析备料标签：materialNo:xxx;supplierId:xxx;num:xxx */
function parseTagQr(text) {
  const result = {}
  const cleaned = String(text || '')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/[\r\n\t]/g, '')
    .replace(/；/g, ';')
    .replace(/：/g, ':')
    .trim()
  if (!cleaned) return result
  cleaned.split(';').forEach((pair) => {
    const idx = pair.indexOf(':')
    if (idx === -1) return
    const key = pair.slice(0, idx).trim()
    const value = pair.slice(idx + 1).trim()
    if (key) result[key] = value
  })
  return result
}

function isCompleteTagQr(text) {
  const t = String(text || '')
  return /materialNo\s*[:：]/.test(t) && /supplierId\s*[:：]/.test(t)
}

/** 兼容非标准 JSON：{separator: "]", materialNoIndex: 3} */
function tryParseJsonLoose(s) {
  if (!s) return null
  if (typeof s !== 'string') return null
  const trimmed = s.trim()
  if (!trimmed) return null
  try {
    return JSON.parse(trimmed)
  } catch (e) {
    try {
      const normalized = trimmed
        .replace(/\r?\n/g, ' ')
        .replace(/([{,]\s*)([A-Za-z_][A-Za-z0-9_]*)\s*:/g, '$1"$2":')
      return JSON.parse(normalized)
    } catch (e2) {
      return null
    }
  }
}

function normalizeQrConfig(raw) {
  const parsed = tryParseJsonLoose(raw)
  if (!parsed) return null

  const materialNoIndex = Number(
    parsed.materialNoIndex ?? parsed.materialCodeIndex ?? parsed.materialNodex
  )
  const materialNumIndex = Number(
    parsed.materialNumIndex ?? parsed.qtyIndex
  )
  const supplierCodeIndex = Number(parsed.supplierCodeIndex)
  const batchNoIndex = Number(parsed.batchNoIndex)
  // boxIndex：箱码唯一标识下标
  const boxIndexIndex = Number(
    parsed.boxIndexIndex ??
      (Number.isFinite(Number(parsed.boxIndex)) ? parsed.boxIndex : NaN)
  )
  // 二级截断符：如 键#值$ 场景，先按 # 取段，再按 $ 截取真正的值
  const valueEndSeparator =
    parsed.valueEndSeparator ??
    parsed.endSeparator ??
    parsed.trimSeparator ??
    ''

  return {
    separator: parsed.separator != null ? String(parsed.separator) : '|',
    valueEndSeparator:
      valueEndSeparator != null && String(valueEndSeparator) !== ''
        ? String(valueEndSeparator)
        : '',
    supplierCodeIndex: Number.isFinite(supplierCodeIndex)
      ? supplierCodeIndex
      : -1,
    materialNoIndex: Number.isFinite(materialNoIndex) ? materialNoIndex : -1,
    materialNumIndex: Number.isFinite(materialNumIndex) ? materialNumIndex : -1,
    batchNoIndex: Number.isFinite(batchNoIndex) ? batchNoIndex : -1,
    boxIndexIndex: Number.isFinite(boxIndexIndex) ? boxIndexIndex : -1
  }
}

/** 从原文兜底提取 boxIndex（key:value / key=value） */
function extractBoxIndexFromRaw(text) {
  const m = String(text || '').match(/boxIndex\s*[=:：]\s*([^;|\]\s]+)/i)
  return m ? String(m[1] || '').trim() : ''
}

function parseBoxByConfig(text, cfg) {
  const sep = String(cfg?.separator || '|')
  const endSep = String(cfg?.valueEndSeparator || '')
  const parts = String(text || '').split(sep)
  const pick = (index) => {
    if (!Number.isFinite(index) || index < 0) return ''
    let val = String(parts[index] ?? '').trim()
    // 例如取到 602003098AA$11，再按 $ 截成 602003098AA
    if (endSep && val.includes(endSep)) {
      val = val.split(endSep)[0].trim()
    }
    return val
  }
  const boxIndexFromCfg = pick(cfg.boxIndexIndex)
  const boxIndex = boxIndexFromCfg || extractBoxIndexFromRaw(text)
  return {
    supplierCode: pick(cfg.supplierCodeIndex),
    materialNo: pick(cfg.materialNoIndex),
    num: pick(cfg.materialNumIndex),
    batchNo: pick(cfg.batchNoIndex),
    boxIndex,
    parts
  }
}

function toPositiveNumber(val) {
  if (val === '' || val == null) return null
  const n = Number(val)
  return Number.isFinite(n) ? n : null
}

function formatImageUrl(url) {
  if (!url) return ''
  return url
    .trim()
    .replace(/`/g, '')
    .replace(
      'wms-zwl.cn-south-1.myhuaweicloud.com',
      'wms-zwl.obs.cn-south-1.myhuaweicloud.com'
    )
}

let resultSeq = 0

export default {
  name: 'WareLocationCheck',
  components: { CameraScan },
  setup() {
    const router = useRouter()

    const currentStep = ref(1)
    const tagInput = ref('')
    const boxInput = ref('')
    const tagInfo = ref(null)
    const supplierInfo = ref(null)
    const qrConfig = ref(null)
    const checkResults = ref([])
    const tagInputRef = ref(null)
    const boxInputRef = ref(null)

    const scanVisible = ref(false)
    const scanTarget = ref('') // 'tag' | 'box'
    const scanFormats = SCAN_FORMATS

    const tagLoading = ref(false)
    const materialId = ref('')

    const imageDialogVisible = ref(false)
    const imageLoading = ref(false)
    const materialImageUrl = ref('')
    const currentImageMaterialNo = ref('')

    const qrConfigReady = computed(() => Boolean(qrConfig.value))

    const hasQtyRule = computed(
      () => Number(qrConfig.value?.materialNumIndex) >= 0
    )

    const tagQtyLimit = computed(() => {
      const n = toPositiveNumber(tagInfo.value?.num)
      return n == null ? 0 : n
    })

    // 仅统计匹配成功的数量
    const scannedQtySum = computed(() =>
      checkResults.value.reduce((sum, item) => {
        if (!item?.matched) return sum
        const n = toPositiveNumber(item.num)
        return sum + (n == null ? 0 : n)
      }, 0)
    )

    const scanTitle = computed(() =>
      scanTarget.value === 'tag' ? '扫备料标签' : '扫箱码'
    )

    const displayImageUrl = computed(() =>
      formatImageUrl(materialImageUrl.value)
    )

    /** 失焦收起软键盘，避免挡住扫描区域 */
    const blurInputs = () => {
      nextTick(() => {
        tagInputRef.value?.blur?.()
        boxInputRef.value?.blur?.()
        if (document.activeElement?.blur) {
          document.activeElement.blur()
        }
      })
    }

    const resetForm = () => {
      tagInfo.value = null
      supplierInfo.value = null
      qrConfig.value = null
      checkResults.value = []
      tagInput.value = ''
      boxInput.value = ''
      tagLoading.value = false
      materialId.value = ''
      currentStep.value = 1
    }

    // 扫码提示音
    let audioContext = null
    const initAudioContext = () => {
      if (audioContext) return
      try {
        const Ctor = window.AudioContext || window.webkitAudioContext
        if (!Ctor) return
        audioContext = new Ctor()
        if (audioContext.state === 'suspended') {
          audioContext.resume().catch(() => {})
        }
      } catch (e) {
        console.warn('创建音频上下文失败', e)
      }
    }

    const ensureAudioReady = () => {
      initAudioContext()
      if (!audioContext) return null
      if (audioContext.state === 'suspended') {
        audioContext.resume().catch(() => {})
      }
      return audioContext
    }

    const playSuccessBeep = () => {
      try {
        const ctx = ensureAudioReady()
        if (!ctx) return
        const oscillator = ctx.createOscillator()
        const gainNode = ctx.createGain()
        oscillator.connect(gainNode)
        gainNode.connect(ctx.destination)
        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(1000, ctx.currentTime)
        gainNode.gain.setValueAtTime(1, ctx.currentTime)
        oscillator.start()
        oscillator.stop(ctx.currentTime + 0.3)
        oscillator.onended = () => {
          oscillator.disconnect()
          gainNode.disconnect()
        }
      } catch (e) {
        console.warn('播放成功提示音失败', e)
      }
    }

    const playErrorBeep = () => {
      try {
        const ctx = ensureAudioReady()
        if (!ctx) return
        const oscillator = ctx.createOscillator()
        const gainNode = ctx.createGain()
        oscillator.connect(gainNode)
        gainNode.connect(ctx.destination)
        oscillator.type = 'square'
        const t = ctx.currentTime
        oscillator.frequency.setValueAtTime(400, t)
        gainNode.gain.setValueAtTime(0, t)
        gainNode.gain.setValueAtTime(0.8, t)
        gainNode.gain.setValueAtTime(0, t + 0.1)
        gainNode.gain.setValueAtTime(0.8, t + 0.16)
        gainNode.gain.setValueAtTime(0, t + 0.26)
        gainNode.gain.setValueAtTime(0.8, t + 0.32)
        gainNode.gain.setValueAtTime(0, t + 0.42)
        oscillator.start(t)
        oscillator.stop(t + 0.44)
        oscillator.onended = () => {
          oscillator.disconnect()
          gainNode.disconnect()
        }
      } catch (e) {
        console.warn('播放失败提示音失败', e)
      }
    }

    const handleBack = () => router.back()

    const openScan = (target) => {
      if (tagLoading.value) return
      initAudioContext()
      scanTarget.value = target
      scanVisible.value = true
    }

    onMounted(() => {
      document.addEventListener('click', initAudioContext, { once: true })
      document.addEventListener('touchstart', initAudioContext, { once: true })
      // 进入页不自动聚焦；并把滚动复位到顶部，避免出现「输入框被顶栏挡住」
      nextTick(() => {
        blurInputs()
        window.scrollTo(0, 0)
        const contentEl = document.querySelector('.page .content')
        if (contentEl) contentEl.scrollTop = 0
      })
    })

    onUnmounted(() => {
      document.removeEventListener('click', initAudioContext)
      document.removeEventListener('touchstart', initAudioContext)
      if (audioContext) {
        audioContext.close().catch(() => {})
        audioContext = null
      }
    })

    const fetchSupplierConfig = async (supplierId) => {
      const res = await getSupplierPageGtZero({
        id: supplierId,
        current: 1,
        size: 20
      })
      const records = res?.data?.records || []
      const matched =
        records.find((r) => String(r?.id) === String(supplierId)) ||
        records[0] ||
        null
      return matched
    }

    const applyTagScan = async (rawText) => {
      if (tagLoading.value) return false
      const text = String(rawText || '').trim()
      if (!text) {
        playErrorBeep()
        ElMessage.error('扫码内容为空')
        return false
      }

      const info = parseTagQr(text)
      if (!info.materialNo || !info.supplierId) {
        playErrorBeep()
        tagInput.value = ''
        ElMessage.error('请扫码正确的备料标签')
        blurInputs()
        return false
      }

      tagInput.value = ''
      tagInfo.value = {
        materialNo: String(info.materialNo).trim(),
        supplierId: String(info.supplierId).trim(),
        num: info.num != null ? String(info.num).trim() : ''
      }
      supplierInfo.value = null
      qrConfig.value = null
      checkResults.value = []
      materialId.value = ''
      currentStep.value = 1

      tagLoading.value = true
      try {
        const supplier = await fetchSupplierConfig(tagInfo.value.supplierId)
        if (!supplier) {
          playErrorBeep()
          ElMessage.error(
            `未找到供应商（ID：${tagInfo.value.supplierId}），请检查标签`
          )
          return false
        }

        supplierInfo.value = supplier
        const rawConfig = supplier.supplierQrConfig
        if (
          rawConfig == null ||
          String(rawConfig).trim() === '' ||
          String(rawConfig).trim() === 'null'
        ) {
          playErrorBeep()
          qrConfig.value = null
          ElMessage.error(
            `供应商「${supplier.supplierName || tagInfo.value.supplierId}」未配置二维码解析规则（supplierQrConfig 为空）`
          )
          return false
        }

        const cfg = normalizeQrConfig(rawConfig)
        if (!cfg || cfg.materialNoIndex < 0) {
          playErrorBeep()
          qrConfig.value = null
          ElMessage.error(
            `供应商「${supplier.supplierName || ''}」二维码规则无效，缺少 materialNoIndex`
          )
          return false
        }

        qrConfig.value = cfg
        currentStep.value = 2
        playSuccessBeep()
        ElMessage.success('备料标签识别成功，请扫箱码')
        blurInputs()
        return true
      } catch (e) {
        console.error('查询供应商配置失败', e)
        playErrorBeep()
        ElMessage.error(e?.message || '查询供应商配置失败')
        return false
      } finally {
        tagLoading.value = false
      }
    }

    const applyBoxScan = (rawText) => {
      if (tagLoading.value) return
      if (!tagInfo.value || !qrConfig.value) {
        playErrorBeep()
        ElMessage.warning('请先扫描备料标签并加载二维码规则')
        return
      }

      const text = String(rawText || '')
        .replace(/[\r\n\t]/g, '')
        .trim()
      if (!text) {
        playErrorBeep()
        ElMessage.error('扫码内容为空')
        return
      }

      const parsed = parseBoxByConfig(text, qrConfig.value)
      const expected = String(tagInfo.value.materialNo || '').trim()
      const actual = String(parsed.materialNo || '').trim()
      const boxIndex = String(parsed.boxIndex || '').trim()
      const boxNumStr = String(parsed.num || '').trim()
      const boxNum = toPositiveNumber(boxNumStr)

      // 箱码（boxIndex）去重：同一备料标签下不可重复扫
      if (boxIndex) {
        const duplicated = checkResults.value.some(
          (item) => String(item?.boxIndex || '').trim() === boxIndex
        )
        if (duplicated) {
          playErrorBeep()
          boxInput.value = ''
          ElMessage.warning(`箱码「${boxIndex}」已扫描，请勿重复扫描`)
          blurInputs()
          return
        }
      }

      const pushFail = (failReason, extra = {}) => {
        playErrorBeep()
        boxInput.value = ''
        checkResults.value.unshift({
          id: ++resultSeq,
          raw: text,
          materialNo: actual,
          boxIndex,
          num: boxNumStr,
          matched: false,
          failReason,
          ...extra
        })
        ElMessage.error(failReason)
        blurInputs()
      }

      if (!actual) {
        pushFail('箱码解析失败：未解析到物料编码，请检查二维码规则或箱码内容')
        return
      }

      if (actual !== expected) {
        pushFail(
          `物料编码不匹配：箱码「${actual}」≠ 标签「${expected}」`
        )
        return
      }

      // 规则配置了数量下标时，校验数量并累计不超过备料标签数量
      if (hasQtyRule.value) {
        if (boxNum == null) {
          pushFail('箱码解析失败：未解析到有效数量')
          return
        }
        if (tagQtyLimit.value <= 0) {
          pushFail('备料标签缺少有效数量，无法校验累计数量')
          return
        }
        const nextSum = scannedQtySum.value + boxNum
        if (nextSum > tagQtyLimit.value) {
          pushFail(
            `数量超限：已扫 ${scannedQtySum.value} + 本箱 ${boxNum} = ${nextSum}，超过标签数量 ${tagQtyLimit.value}`
          )
          return
        }
      }

      checkResults.value.unshift({
        id: ++resultSeq,
        raw: text,
        materialNo: actual,
        boxIndex,
        num: hasQtyRule.value ? boxNum : boxNumStr,
        matched: true,
        failReason: ''
      })
      boxInput.value = ''
      playSuccessBeep()

      if (hasQtyRule.value) {
        ElMessage.success(
          `匹配成功 ${actual}${boxIndex ? ` / ${boxIndex}` : ''}，${boxNum}（${scannedQtySum.value}/${tagQtyLimit.value}）`
        )
      } else {
        ElMessage.success(
          `匹配成功 ${actual}${boxIndex ? ` / ${boxIndex}` : ''}`
        )
      }
      blurInputs()
    }

    let tagScanTimer = null
    watch(tagInput, (val) => {
      if (tagScanTimer) clearTimeout(tagScanTimer)
      if (tagLoading.value) return
      const text = String(val || '').trim()
      if (!text || !isCompleteTagQr(text)) return
      tagScanTimer = setTimeout(() => {
        if (tagLoading.value) return
        const current = String(tagInput.value || '').trim()
        if (current === text && isCompleteTagQr(current)) {
          applyTagScan(current)
        }
      }, 600)
    })

    let boxScanTimer = null
    watch(boxInput, (val) => {
      if (boxScanTimer) clearTimeout(boxScanTimer)
      const text = String(val || '').trim()
      if (!text || !tagInfo.value || !qrConfig.value) return
      boxScanTimer = setTimeout(() => {
        if (String(boxInput.value || '').trim() === text) {
          applyBoxScan(text)
        }
      }, 350)
    })

    const handleScanSuccess = (decodedText) => {
      scanVisible.value = false
      if (scanTarget.value === 'tag') {
        applyTagScan(decodedText)
      } else if (scanTarget.value === 'box') {
        applyBoxScan(decodedText)
      }
      scanTarget.value = ''
    }

    const handleTagManual = () => {
      if (tagLoading.value) return
      if (tagScanTimer) {
        clearTimeout(tagScanTimer)
        tagScanTimer = null
      }
      const text = String(tagInput.value || '').trim()
      if (!text) return
      applyTagScan(text)
    }

    const handleBoxManual = () => {
      if (boxScanTimer) {
        clearTimeout(boxScanTimer)
        boxScanTimer = null
      }
      const text = String(boxInput.value || '').trim()
      if (!text) return
      applyBoxScan(text)
    }

    const removeResult = (idx) => {
      checkResults.value.splice(idx, 1)
    }

    const handleReset = () => {
      resetForm()
      blurInputs()
      ElMessage.info('已重置')
    }

    const handleQueryImage = async () => {
      if (!tagInfo.value?.materialNo) {
        ElMessage.error('缺少物料编码')
        return
      }
      imageDialogVisible.value = true
      imageLoading.value = true
      materialImageUrl.value = ''
      currentImageMaterialNo.value = tagInfo.value.materialNo || ''
      try {
        let id = materialId.value
        if (!id) {
          const pageRes = await getMaterialPage({
            materialNo: tagInfo.value.materialNo,
            supplierId: tagInfo.value.supplierId,
            current: 1,
            size: 20,
            isCompose: 0
          })
          const records = pageRes?.data?.records || []
          const hit =
            records.find(
              (r) =>
                String(r?.materialNo || '').trim() ===
                String(tagInfo.value.materialNo).trim()
            ) || records[0]
          id = hit?.id || ''
          materialId.value = id
        }
        if (!id) {
          materialImageUrl.value = ''
          return
        }
        const res = await service.get('/uic-system/material/detail', {
          params: { id }
        })
        materialImageUrl.value =
          res?.success && res?.data ? res.data.image || '' : ''
      } catch (err) {
        console.error('查询物料图片失败', err)
        ElMessage.error('查询物料图片失败')
        materialImageUrl.value = ''
      } finally {
        imageLoading.value = false
      }
    }

    return {
      currentStep,
      tagInput,
      tagInputRef,
      boxInput,
      boxInputRef,
      tagInfo,
      supplierInfo,
      qrConfigReady,
      hasQtyRule,
      tagQtyLimit,
      scannedQtySum,
      checkResults,
      scanVisible,
      scanTitle,
      scanFormats,
      tagLoading,
      imageDialogVisible,
      imageLoading,
      currentImageMaterialNo,
      displayImageUrl,
      handleBack,
      openScan,
      handleScanSuccess,
      handleTagManual,
      handleBoxManual,
      removeResult,
      handleReset,
      handleQueryImage
    }
  }
}
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
  min-height: 100vh;
  min-height: 100dvh;
  background: var(--background-color);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  overflow: hidden;
  box-sizing: border-box;
}

.header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  padding-top: calc(12px + env(safe-area-inset-top));
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px rgba(35, 177, 192, 0.18);
  /* 不用 fixed，避免键盘弹出时内容滚到顶栏下面被挡住 */
  position: relative;
  z-index: 10;
  height: calc(52px + env(safe-area-inset-top));
  box-sizing: border-box;
}

.back-btn {
  width: 36px;
  height: 36px;
  padding: 0;
  color: var(--header-text);
}

.back-icon {
  width: 22px;
  height: 22px;
}

.title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  flex: 1;
  text-align: center;
  color: var(--header-text);
}

.header-right {
  width: 36px;
}

.content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 10px 12px;
  padding-bottom: 12px;
  margin-top: 0;
}

.steps {
  padding: 4px 0 8px;
}

.steps :deep(.el-step__icon) {
  width: 22px;
  height: 22px;
  font-size: 12px;
}

.steps :deep(.el-step__title) {
  font-size: 12px;
  line-height: 1.2;
  margin-top: 2px !important;
}

.steps :deep(.el-step__title.is-process),
.steps :deep(.el-step__title.is-finish),
.steps :deep(.el-step__title.is-wait) {
  font-weight: 500;
}

.steps :deep(.el-step__head) {
  margin-bottom: 0;
}

.steps :deep(.el-step.is-horizontal .el-step__line) {
  top: 11px;
}

.steps :deep(.el-step__main) {
  padding: 0;
}

.section {
  margin-bottom: 10px;
  border-radius: 10px;
  border: 1px solid var(--border-color);
}

.section :deep(.el-card__header) {
  padding: 10px 12px;
}

.section :deep(.el-card__body) {
  padding: 12px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.section-sub {
  font-size: 12px;
  color: var(--text-secondary);
  font-weight: 400;
}

.scan-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.scan-input {
  flex: 8 8 auto;
  width: 80%;
}

.scan-input :deep(.el-input__wrapper) {
  border-radius: 8px;
}

.scan-btn {
  flex: 2 2 auto;
  width: 20%;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.scan-icon {
  width: 20px;
  height: 20px;
  filter: brightness(0) invert(1);
}

.info-desc {
  margin-top: 12px;
}

.material-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.material-no {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: var(--text-primary);
  word-break: break-all;
}

.material-image-btn {
  flex: 0 0 auto;
}

.qty-ok {
  color: var(--primary-color);
  font-weight: 600;
}

.qty-full {
  color: #67c23a;
  font-weight: 600;
}

.scanned-list {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.scanned-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: var(--background-color);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.scanned-item.fail {
  border-color: rgba(245, 108, 108, 0.45);
}

.scanned-idx {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--primary-color);
  color: #fff;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.scanned-idx.fail {
  background: #f56c6c;
}

.scanned-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.scanned-code {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  word-break: break-all;
}

.scanned-raw {
  font-size: 11px;
  color: var(--text-secondary);
  word-break: break-all;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scanned-fail-reason {
  font-size: 11px;
  color: #f56c6c;
  word-break: break-all;
}

.scanned-del {
  flex: 0 0 auto;
  font-size: 18px;
  color: var(--text-secondary);
  padding: 0 4px;
}

.bottom-bar {
  flex-shrink: 0;
  padding: 10px 12px;
  padding-bottom: calc(10px + env(safe-area-inset-bottom));
  background: var(--card-background);
  border-top: 1px solid var(--border-color);
}

.bottom-btn {
  width: 100%;
  height: 44px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 0;
}

.bottom-btn.secondary {
  --el-button-bg-color: var(--card-background);
  --el-button-text-color: var(--primary-color);
  --el-button-border-color: var(--primary-color);
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  background: var(--card-background);
}

.material-image-dialog :deep(.el-dialog__body) {
  padding: 12px;
}

.image-loading {
  min-height: 100px;
}

.image-material-info {
  display: flex;
  align-items: center;
  padding: 8px 10px;
  background: var(--background-color);
  border-radius: 6px;
  margin-bottom: 10px;
  font-size: 13px;
}

.image-material-label {
  color: var(--text-secondary);
  margin-right: 6px;
}

.image-material-value {
  color: var(--text-primary);
  font-weight: 600;
}

.image-preview-area {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.material-image {
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
}

.material-image :deep(.el-image__inner) {
  max-height: 300px;
  border-radius: 8px;
}

.image-zoom-hint {
  margin-top: 8px;
  font-size: 12px;
  color: var(--text-secondary);
}
</style>
